import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLocale } from '@platform/core';
import { useBudget } from '../contexts/BudgetContext';
import { SubjectsTree } from '../components/SubjectsTree';
import { BudgetSubject, BudgetSection } from '../types/budget.types';

type TabType = 'settings' | 'data' | 'charts' | 'dashboard';

export const ProjectDetailPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { t, direction } = useLocale();
  const {
    projects,
    projectEntries,
    addProjectSubject,
    updateProjectSubject,
    removeProjectSubject,
    addProjectSection,
    updateProjectSection,
    removeProjectSection,
    reorderProjectSubjects,
    regenerateProjectData,
    isLoading,
  } = useBudget();

  const [activeTab, setActiveTab] = useState<TabType>('settings');
  const [projectSubjects, setProjectSubjects] = useState<BudgetSubject[]>([]);
  const [projectSections, setProjectSections] = useState<BudgetSection[]>([]);

  const project = useMemo(() => {
    return projects.find(p => p.id === projectId);
  }, [projects, projectId]);

  // Load project subjects and sections
  useEffect(() => {
    if (project) {
      // Use project's own subjects/sections (projects are independent from budgets)
      setProjectSubjects(project.subjects || []);
      setProjectSections(project.sections || []);
    }
  }, [project]);

  // Calculate total income from credit subjects
  const totalIncome = useMemo(() => {
    return projectSubjects
      .filter(s => s.type === 'credit')
      .reduce((sum, s) => sum + (s.goalPercentage || 0), 0);
  }, [projectSubjects]);

  // Calculate periods count
  const periodsCount = useMemo(() => {
    const intervalType = project?.intervalType || 'monthly';
    if (!project?.startDate || !project?.endDate) {
      return intervalType === 'weekly' ? 52 : 12;
    }
    const start = new Date(project.startDate);
    const end = new Date(project.endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (intervalType === 'weekly') {
      return Math.max(1, Math.ceil(diffDays / 7));
    } else {
      const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
      return Math.max(1, months);
    }
  }, [project]);

  // Subject/Section handlers for SubjectsTree
  const handleAddSubject = async (subject: Omit<BudgetSubject, 'id'>) => {
    if (!projectId) return;
    await addProjectSubject(projectId, subject);
    // Optimistically update local state
    const newSubject = { ...subject, id: `temp-${Date.now()}` };
    setProjectSubjects(prev => [...prev, newSubject]);
  };

  const handleUpdateSubject = async (subjectId: string, updates: Partial<BudgetSubject>) => {
    if (!projectId) return;
    await updateProjectSubject(projectId, subjectId, updates);
    setProjectSubjects(prev => prev.map(s => s.id === subjectId ? { ...s, ...updates } : s));
  };

  const handleRemoveSubject = async (subjectId: string) => {
    if (!projectId) return;
    await removeProjectSubject(projectId, subjectId);
    setProjectSubjects(prev => prev.filter(s => s.id !== subjectId));
    setProjectSections(prev => prev.filter(s => s.subjectId !== subjectId));
  };

  const handleAddSection = async (section: Omit<BudgetSection, 'id'>) => {
    if (!projectId) return;
    await addProjectSection(projectId, section);
    const newSection = { ...section, id: `temp-${Date.now()}` };
    setProjectSections(prev => [...prev, newSection]);
  };

  const handleUpdateSection = async (sectionId: string, updates: Partial<BudgetSection>) => {
    if (!projectId) return;
    await updateProjectSection(projectId, sectionId, updates);
    setProjectSections(prev => prev.map(s => s.id === sectionId ? { ...s, ...updates } : s));
  };

  const handleRemoveSection = async (sectionId: string) => {
    if (!projectId) return;
    await removeProjectSection(projectId, sectionId);
    setProjectSections(prev => prev.filter(s => s.id !== sectionId));
  };

  const handleReorderSubjects = async (subjects: BudgetSubject[]) => {
    if (!projectId) return;
    await reorderProjectSubjects(projectId, subjects);
    setProjectSubjects(subjects);
  };

  const handleGenerateData = async () => {
    if (!projectId) return;
    if (confirm(t('project.confirmGenerateData') || 'Generate data collection? This will create entries for all periods.')) {
      await regenerateProjectData(projectId, true);
      setActiveTab('data');
    }
  };

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>{t('general.loading')}</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="project-detail-page">
        <div className="empty-state">
          <h3>{t('project.notFound') || 'Project not found'}</h3>
          <button className="btn btn-primary" onClick={() => navigate('/projects')}>
            {t('project.backToProjects') || 'Back to Projects'}
          </button>
        </div>
      </div>
    );
  }

  // Project entries for this project
  const entries = projectEntries.filter(e => e.projectId === projectId);

  return (
    <div className="project-detail-page" dir={direction}>
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate('/projects')}>
            ← {t('common.back')}
          </button>
          <h1>{project.name}</h1>
          <span className={`status-badge status-${project.status}`}>
            {t(`budget.projects.status${project.status.charAt(0).toUpperCase() + project.status.slice(1)}`)}
          </span>
        </div>
        <div className="header-right">
          <span className="project-info">
            {project.client && <span>{project.client}</span>}
            {project.contractNumber && <span>#{project.contractNumber}</span>}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          {t('nav.dashboard') || 'Dashboard'}
        </button>
        <button
          className={`tab ${activeTab === 'data' ? 'active' : ''}`}
          onClick={() => setActiveTab('data')}
        >
          {t('nav.data') || 'Data'}
        </button>
        <button
          className={`tab ${activeTab === 'charts' ? 'active' : ''}`}
          onClick={() => setActiveTab('charts')}
        >
          {t('nav.charts') || 'Charts'}
        </button>
        <button
          className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          {t('nav.settings') || 'Settings'}
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'settings' && (
          <div className="settings-tab">
            {/* Project Info Section */}
            <section className="settings-section">
              <h2>{t('project.details') || 'Project Details'}</h2>
              <div className="info-grid">
                <div className="info-item">
                  <label>{t('budget.projects.projectManager')}:</label>
                  <span>{project.projectManager || '-'}</span>
                </div>
                <div className="info-item">
                  <label>{t('budget.projects.manager')}:</label>
                  <span>{project.manager || '-'}</span>
                </div>
                <div className="info-item">
                  <label>{t('budget.projects.client')}:</label>
                  <span>{project.client || '-'}</span>
                </div>
                <div className="info-item">
                  <label>{t('budget.projects.contract')}:</label>
                  <span>{project.contractNumber || '-'}</span>
                </div>
                <div className="info-item">
                  <label>{t('budget.projects.startDate') || 'Start Date'}:</label>
                  <span>{project.startDate || '-'}</span>
                </div>
                <div className="info-item">
                  <label>{t('budget.projects.endDate') || 'End Date'}:</label>
                  <span>{project.endDate || '-'}</span>
                </div>
              </div>
            </section>

            {/* Subjects Tree Section */}
            <section className="settings-section">
              <h2>{t('settings.subjects') || 'Subjects & Goals'}</h2>
              <SubjectsTree
                subjects={projectSubjects}
                sections={projectSections}
                currency={project.currency || 'ILS'}
                intervalType={project.intervalType || 'monthly'}
                totalIncome={totalIncome}
                periodsCount={periodsCount}
                onAddSubject={handleAddSubject}
                onUpdateSubject={handleUpdateSubject}
                onRemoveSubject={handleRemoveSubject}
                onAddSection={handleAddSection}
                onUpdateSection={handleUpdateSection}
                onRemoveSection={handleRemoveSection}
                onReorderSubjects={handleReorderSubjects}
                showGoals={true}
              />
            </section>

            {/* Actions Section */}
            <section className="settings-section actions-section">
              <button className="btn btn-primary" onClick={handleGenerateData}>
                {t('settings.createDataCollection') || 'Generate Data Collection'}
              </button>
              <button className="btn btn-secondary" onClick={() => navigate(`/projects`)}>
                {t('common.back')}
              </button>
            </section>
          </div>
        )}

        {activeTab === 'data' && (
          <div className="data-tab">
            <ProjectDataView
              project={project}
              subjects={projectSubjects}
              sections={projectSections}
              entries={entries}
              currency={project.currency || 'ILS'}
              intervalType={project.intervalType || 'monthly'}
            />
          </div>
        )}

        {activeTab === 'charts' && (
          <div className="charts-tab">
            <ProjectChartsView
              project={project}
              subjects={projectSubjects}
              entries={entries}
              currency={project.currency || 'ILS'}
            />
          </div>
        )}

        {activeTab === 'dashboard' && (
          <div className="dashboard-tab">
            <ProjectDashboardView
              project={project}
              subjects={projectSubjects}
              sections={projectSections}
              entries={entries}
              currency={project.currency || 'ILS'}
            />
          </div>
        )}
      </div>

      <style>{`
        .project-detail-page {
          max-width: 1400px;
          margin: 0 auto;
        }

        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .btn-back {
          background: none;
          border: none;
          color: var(--color-primary);
          cursor: pointer;
          font-size: 0.875rem;
          padding: 0.5rem;
        }

        .btn-back:hover {
          text-decoration: underline;
        }

        .page-header h1 {
          margin: 0;
          font-size: 1.5rem;
        }

        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 999px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .status-active {
          background: #dcfce7;
          color: #166534;
        }

        .status-completed {
          background: #dbeafe;
          color: #1e40af;
        }

        .status-future {
          background: #fef3c7;
          color: #92400e;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .project-info {
          display: flex;
          gap: 1rem;
          font-size: 0.875rem;
          color: var(--color-text-secondary);
        }

        /* Tabs */
        .tabs {
          display: flex;
          gap: 0;
          border-bottom: 2px solid var(--color-border);
          margin-bottom: 1.5rem;
        }

        .tab {
          padding: 0.75rem 1.5rem;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 0.875rem;
          color: var(--color-text-secondary);
          border-bottom: 2px solid transparent;
          margin-bottom: -2px;
          transition: all 0.2s;
        }

        .tab:hover {
          color: var(--color-text);
        }

        .tab.active {
          color: var(--color-primary);
          border-bottom-color: var(--color-primary);
          font-weight: 500;
        }

        /* Tab Content */
        .tab-content {
          min-height: 400px;
        }

        .settings-section {
          background: var(--color-surface);
          border-radius: var(--radius-lg);
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          box-shadow: var(--shadow);
        }

        .settings-section h2 {
          margin: 0 0 1rem;
          font-size: 1.125rem;
          border-bottom: 1px solid var(--color-border);
          padding-bottom: 0.5rem;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 1rem;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .info-item label {
          font-size: 0.75rem;
          color: var(--color-text-secondary);
          text-transform: uppercase;
        }

        .info-item span {
          font-size: 0.875rem;
        }

        .actions-section {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
        }

        .btn {
          padding: 0.5rem 1rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          cursor: pointer;
          font-size: 0.875rem;
        }

        .btn-primary {
          background: var(--color-primary);
          color: white;
          border-color: var(--color-primary);
        }

        .btn-secondary {
          background: var(--color-surface);
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background: var(--color-surface);
          border-radius: var(--radius-lg);
        }

        .loading-screen {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 300px;
          gap: 1rem;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--color-border);
          border-top-color: var(--color-primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

// Project Data View Component
const ProjectDataView: React.FC<{
  project: any;
  subjects: BudgetSubject[];
  sections: BudgetSection[];
  entries: any[];
  currency?: string;
  intervalType?: string;
}> = ({ project, subjects, sections, entries, currency, intervalType }) => {
  const { t } = useLocale();

  // Group entries by period
  const periods = useMemo(() => {
    const periodSet = new Set(entries.map(e => e.period));
    return Array.from(periodSet).sort();
  }, [entries]);

  // Get measures from entries
  const measures = useMemo(() => {
    const measureSet = new Set(entries.map(e => e.measure));
    return Array.from(measureSet);
  }, [entries]);

  if (entries.length === 0) {
    return (
      <div className="empty-data">
        <p>{t('project.noData') || 'No data entries yet. Generate data collection from Settings tab.'}</p>
      </div>
    );
  }

  return (
    <div className="data-view">
      <div className="data-table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('settings.subject') || 'Subject'}</th>
              <th>{t('settings.section') || 'Section'}</th>
              {periods.map(period => (
                <th key={period} colSpan={measures.length}>{period}</th>
              ))}
            </tr>
            <tr>
              <th></th>
              <th></th>
              {periods.flatMap(period =>
                measures.map(measure => (
                  <th key={`${period}-${measure}`} className="measure-header">
                    {measure === 'actual' ? 'בפועל' : measure === 'budget' ? 'יעד' : measure}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {subjects.filter(s => s.type !== 'calculated').map(subject => {
              const subjectSections = sections.filter(s => s.subjectId === subject.id);

              if (subjectSections.length === 0) {
                return (
                  <tr key={subject.id} className={`subject-row type-${subject.type}`}>
                    <td className="subject-cell">{subject.name}</td>
                    <td>-</td>
                    {periods.flatMap(period =>
                      measures.map(measure => {
                        const entry = entries.find(e =>
                          e.period === period &&
                          e.subjectId === subject.name &&
                          e.measure === measure
                        );
                        return (
                          <td key={`${period}-${measure}`} className="data-cell">
                            {entry?.amount || '-'}
                          </td>
                        );
                      })
                    )}
                  </tr>
                );
              }

              return subjectSections.map((section, idx) => (
                <tr key={section.id} className={`section-row type-${subject.type}`}>
                  {idx === 0 && (
                    <td className="subject-cell" rowSpan={subjectSections.length}>
                      {subject.name}
                    </td>
                  )}
                  <td className="section-cell">{section.name}</td>
                  {periods.flatMap(period =>
                    measures.map(measure => {
                      const entry = entries.find(e =>
                        e.period === period &&
                        e.subjectId === subject.name &&
                        e.sectionId === section.name &&
                        e.measure === measure
                      );
                      return (
                        <td key={`${period}-${measure}`} className="data-cell">
                          {entry?.amount || '-'}
                        </td>
                      );
                    })
                  )}
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>

      <style>{`
        .data-view {
          background: var(--color-surface);
          border-radius: var(--radius-lg);
          padding: 1rem;
          box-shadow: var(--shadow);
        }

        .data-table-wrapper {
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }

        .data-table th,
        .data-table td {
          padding: 0.5rem;
          border: 1px solid var(--color-border);
          text-align: center;
        }

        .data-table th {
          background: var(--color-background);
          font-weight: 500;
        }

        .measure-header {
          font-size: 0.75rem;
          font-weight: 400;
        }

        .subject-cell {
          font-weight: 500;
          text-align: start;
          background: var(--color-background);
        }

        .section-cell {
          text-align: start;
          padding-inline-start: 1.5rem;
        }

        .type-credit .subject-cell {
          border-inline-start: 3px solid #22c55e;
        }

        .type-debit .subject-cell {
          border-inline-start: 3px solid #ef4444;
        }

        .data-cell {
          min-width: 60px;
        }

        .empty-data {
          text-align: center;
          padding: 3rem;
          color: var(--color-text-secondary);
        }
      `}</style>
    </div>
  );
};

// Project Charts View Component
const ProjectChartsView: React.FC<{
  project: any;
  subjects: BudgetSubject[];
  entries: any[];
  currency?: string;
}> = ({ project, subjects, entries, currency }) => {
  const { t } = useLocale();

  // Calculate totals by period for chart
  const chartData = useMemo(() => {
    const periods = [...new Set(entries.map(e => e.period))].sort();

    return periods.map(period => {
      const periodEntries = entries.filter(e => e.period === period);

      const income = periodEntries
        .filter(e => {
          const subject = subjects.find(s => s.name === e.subjectId || s.id === e.subjectId);
          return subject?.type === 'credit' && e.measure === 'actual';
        })
        .reduce((sum, e) => sum + e.amount, 0);

      const expenses = periodEntries
        .filter(e => {
          const subject = subjects.find(s => s.name === e.subjectId || s.id === e.subjectId);
          return subject?.type === 'debit' && e.measure === 'actual';
        })
        .reduce((sum, e) => sum + e.amount, 0);

      return {
        period,
        income,
        expenses,
        profit: income - expenses,
      };
    });
  }, [entries, subjects]);

  if (entries.length === 0) {
    return (
      <div className="empty-charts">
        <p>{t('project.noDataForCharts') || 'No data available for charts. Add data entries first.'}</p>
      </div>
    );
  }

  // Simple bar chart visualization
  const maxValue = Math.max(...chartData.flatMap(d => [d.income, d.expenses]));

  return (
    <div className="charts-view">
      <div className="chart-container">
        <h3>{t('charts.incomeVsExpenses') || 'Income vs Expenses'}</h3>
        <div className="simple-chart">
          {chartData.map(data => (
            <div key={data.period} className="chart-bar-group">
              <div className="bar-label">{data.period}</div>
              <div className="bars">
                <div
                  className="bar bar-income"
                  style={{ width: `${(data.income / maxValue) * 100}%` }}
                  title={`Income: ${data.income.toLocaleString()}`}
                />
                <div
                  className="bar bar-expenses"
                  style={{ width: `${(data.expenses / maxValue) * 100}%` }}
                  title={`Expenses: ${data.expenses.toLocaleString()}`}
                />
              </div>
              <div className="bar-values">
                <span className="income-value">{data.income.toLocaleString()}</span>
                <span className="expense-value">{data.expenses.toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="chart-legend">
          <span className="legend-item"><span className="legend-color income"></span> {t('budget.income') || 'Income'}</span>
          <span className="legend-item"><span className="legend-color expenses"></span> {t('budget.expenses') || 'Expenses'}</span>
        </div>
      </div>

      <style>{`
        .charts-view {
          display: grid;
          gap: 1.5rem;
        }

        .chart-container {
          background: var(--color-surface);
          border-radius: var(--radius-lg);
          padding: 1.5rem;
          box-shadow: var(--shadow);
        }

        .chart-container h3 {
          margin: 0 0 1rem;
          font-size: 1rem;
        }

        .simple-chart {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .chart-bar-group {
          display: grid;
          grid-template-columns: 60px 1fr 120px;
          gap: 0.5rem;
          align-items: center;
        }

        .bar-label {
          font-size: 0.75rem;
          color: var(--color-text-secondary);
        }

        .bars {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .bar {
          height: 16px;
          border-radius: 2px;
          min-width: 4px;
        }

        .bar-income {
          background: #22c55e;
        }

        .bar-expenses {
          background: #ef4444;
        }

        .bar-values {
          display: flex;
          flex-direction: column;
          font-size: 0.75rem;
        }

        .income-value {
          color: #166534;
        }

        .expense-value {
          color: #991b1b;
        }

        .chart-legend {
          display: flex;
          gap: 1.5rem;
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid var(--color-border);
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
        }

        .legend-color {
          width: 16px;
          height: 16px;
          border-radius: 2px;
        }

        .legend-color.income {
          background: #22c55e;
        }

        .legend-color.expenses {
          background: #ef4444;
        }

        .empty-charts {
          text-align: center;
          padding: 3rem;
          color: var(--color-text-secondary);
          background: var(--color-surface);
          border-radius: var(--radius-lg);
        }
      `}</style>
    </div>
  );
};

// Project Dashboard View Component
const ProjectDashboardView: React.FC<{
  project: any;
  subjects: BudgetSubject[];
  sections: BudgetSection[];
  entries: any[];
  currency?: string;
}> = ({ project, subjects, sections, entries, currency }) => {
  const { t } = useLocale();

  // Calculate summary statistics
  const stats = useMemo(() => {
    const totalIncome = entries
      .filter(e => {
        const subject = subjects.find(s => s.name === e.subjectId || s.id === e.subjectId);
        return subject?.type === 'credit' && e.measure === 'actual';
      })
      .reduce((sum, e) => sum + e.amount, 0);

    const totalExpenses = entries
      .filter(e => {
        const subject = subjects.find(s => s.name === e.subjectId || s.id === e.subjectId);
        return subject?.type === 'debit' && e.measure === 'actual';
      })
      .reduce((sum, e) => sum + e.amount, 0);

    const profit = totalIncome - totalExpenses;
    const profitPercent = totalIncome > 0 ? (profit / totalIncome) * 100 : 0;

    // Time progress
    const now = Date.now();
    const start = new Date(project.startDate).getTime();
    const end = new Date(project.endDate).getTime();
    const timeProgress = end > start ? Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100)) : 0;

    // Budget progress
    const budgetProgress = project.budget > 0 ? (totalExpenses / project.budget) * 100 : 0;

    return {
      totalIncome,
      totalExpenses,
      profit,
      profitPercent,
      timeProgress,
      budgetProgress,
      invoicesProgress: project.budget > 0 ? (totalIncome / project.budget) * 100 : 0,
    };
  }, [entries, subjects, project]);

  const getCurrencySymbol = (c?: string) => {
    switch (c) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      default: return '₪';
    }
  };

  return (
    <div className="dashboard-view">
      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-label">{t('budget.totalIncome') || 'Total Income'}</div>
          <div className="card-value income">
            {getCurrencySymbol(currency)}{stats.totalIncome.toLocaleString()}
          </div>
        </div>
        <div className="summary-card">
          <div className="card-label">{t('budget.totalExpenses') || 'Total Expenses'}</div>
          <div className="card-value expenses">
            {getCurrencySymbol(currency)}{stats.totalExpenses.toLocaleString()}
          </div>
        </div>
        <div className="summary-card">
          <div className="card-label">{t('budget.profit') || 'Profit'}</div>
          <div className={`card-value ${stats.profit >= 0 ? 'profit' : 'loss'}`}>
            {getCurrencySymbol(currency)}{stats.profit.toLocaleString()}
            <span className="percent">({stats.profitPercent.toFixed(1)}%)</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-label">{t('budget.projects.budget') || 'Budget'}</div>
          <div className="card-value">
            {getCurrencySymbol(currency)}{project.budget?.toLocaleString() || 0}
          </div>
        </div>
      </div>

      {/* Progress Indicators */}
      <div className="progress-section">
        <h3>{t('project.progress') || 'Progress'}</h3>
        <div className="progress-items">
          <div className="progress-item">
            <div className="progress-label">
              <span>{t('budget.projects.timeConsumed') || 'Time'}</span>
              <span>{stats.timeProgress.toFixed(0)}%</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill time" style={{ width: `${Math.min(100, stats.timeProgress)}%` }} />
            </div>
          </div>
          <div className="progress-item">
            <div className="progress-label">
              <span>{t('budget.projects.budgetConsumed') || 'Budget Used'}</span>
              <span>{stats.budgetProgress.toFixed(0)}%</span>
            </div>
            <div className="progress-bar">
              <div
                className={`progress-fill budget ${stats.budgetProgress > 100 ? 'over' : ''}`}
                style={{ width: `${Math.min(100, stats.budgetProgress)}%` }}
              />
            </div>
          </div>
          <div className="progress-item">
            <div className="progress-label">
              <span>{t('budget.projects.invoicesIssued') || 'Invoiced'}</span>
              <span>{stats.invoicesProgress.toFixed(0)}%</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill invoices" style={{ width: `${Math.min(100, stats.invoicesProgress)}%` }} />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .dashboard-view {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .summary-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .summary-card {
          background: var(--color-surface);
          border-radius: var(--radius-lg);
          padding: 1.5rem;
          box-shadow: var(--shadow);
        }

        .card-label {
          font-size: 0.75rem;
          color: var(--color-text-secondary);
          text-transform: uppercase;
          margin-bottom: 0.5rem;
        }

        .card-value {
          font-size: 1.5rem;
          font-weight: 600;
        }

        .card-value.income {
          color: #166534;
        }

        .card-value.expenses {
          color: #991b1b;
        }

        .card-value.profit {
          color: #166534;
        }

        .card-value.loss {
          color: #991b1b;
        }

        .card-value .percent {
          font-size: 0.875rem;
          font-weight: 400;
          margin-inline-start: 0.5rem;
        }

        .progress-section {
          background: var(--color-surface);
          border-radius: var(--radius-lg);
          padding: 1.5rem;
          box-shadow: var(--shadow);
        }

        .progress-section h3 {
          margin: 0 0 1rem;
          font-size: 1rem;
        }

        .progress-items {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .progress-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .progress-label {
          display: flex;
          justify-content: space-between;
          font-size: 0.875rem;
        }

        .progress-bar {
          height: 8px;
          background: var(--color-background);
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          border-radius: 4px;
          transition: width 0.3s;
        }

        .progress-fill.time {
          background: #3b82f6;
        }

        .progress-fill.budget {
          background: #22c55e;
        }

        .progress-fill.budget.over {
          background: #ef4444;
        }

        .progress-fill.invoices {
          background: #8b5cf6;
        }
      `}</style>
    </div>
  );
};
